<template>
  <Form
    id="account-edit-form"
    ref="form"
    v-slot="{ errors, meta, values }"
    :validation-schema="schema"
    :validate-on-mount="true"
    :initial-values="currentUser"
  >
    <div class="w-full mb-3">
      <label for="">{{
        $t('account.accountInformations.form.email.label')
      }}</label>
      <Field
        v-slot="{ field }"
        v-model="currentUser.email"
        type="email"
        name="email"
      >
        <Input
          v-model="currentUser.email"
          v-bind="field"
          type="email"
          name="email"
          :disabled="false"
          :no-error="true"
          :error="errors.email ? $t(`errors.${errors.email}`) : null"
          :placeholder="
            $t('account.accountTeam.teamUser.addUser.form.email.placeholder')
          "
        />
      </Field>
    </div>

    <div class="flex mb-3">
      <div class="w-full mr-2">
        <label for="">{{
          $t('account.accountInformations.form.firstname.label')
        }}</label>
        <Field
          v-slot="{ field }"
          v-model="currentUser.firstName"
          type="text"
          name="firstName"
          ><Input
            v-model="currentUser.firstName"
            type="text"
            v-bind="field"
            name="firstName"
            :no-error="true"
            :disabled="false"
            :error="
              errors && errors.firstName
                ? $t(`errors.${errors.firstName}`)
                : null
            "
            :placeholder="
              $t(
                'account.accountTeam.teamUser.addUser.form.firstname.placeholder'
              )
            "
        /></Field>
      </div>
      <div class="w-full">
        <label for="">{{
          $t('account.accountInformations.form.lastname.label')
        }}</label>
        <Field
          v-slot="{ field }"
          v-model="currentUser.lastName"
          type="text"
          name="lastName"
          ><Input
            v-model="currentUser.lastName"
            v-bind="field"
            type="text"
            :no-error="true"
            name="lastName"
            :disabled="false"
            :error="
              errors && errors.lastName ? $t(`errors.${errors.lastName}`) : null
            "
            :placeholder="
              $t(
                'account.accountTeam.teamUser.addUser.form.lastname.placeholder'
              )
            "
        /></Field>
      </div>
    </div>

    <div
      v-if="!companyId && roles.length && highestPriority < 2"
      class="w-full text-left flex flex-col my-3"
    >
      <label for="">{{
        $t('account.accountInformations.form.roleAdmin.label')
      }}</label>
      <Field v-slot="{ field }" v-model="currentUser.roles" name="roles">
        <multiselect
          :value="currentUser.roles"
          mode="tags"
          open-direction="bottom"
          :placeholder="$t('account.accountInformations.form.role.placeholder')"
          v-bind="field"
          :options="roles"
          @change="currentUser.roles = $event"
        ></multiselect>
      </Field>
    </div>

    <div
      v-if="hasAdminRole"
      class="flex justify-start items-center w-full py-2 my-2"
    >
      <div class="flex justify-start items-start">
        <Field
          v-model="currentUser.useMFA"
          type="checkbox"
          :value="true"
          name="useMFA"
        >
          <Checkbox
            name="useMFA"
            :checked="currentUser.useMFA"
            @onChange="currentUser.useMFA = $event"
          />
        </Field>
      </div>
      <label class="text-md text-start break-words text-secondary9 ml-2 mb-0">{{
        $t('contactBook.form.useMFA')
      }}</label>
    </div>

    <h4 class="mt-3 text-blackp">
      {{ $t('account.accountInformations.companyRolesHeader') }}
    </h4>
    <FieldArray name="accounts" v-slot="{ fields, push, remove }">
      <div v-for="(data, idx) in fields" :key="data.key" class="flex">
        <div class="w-full text-left flex flex-col mt-3 mr-2">
          <label for="">{{
            $t('account.accountInformations.form.company.label')
          }}</label>
          <DropdownCompany
            v-model="currentUser.accounts[idx].companyId"
            class="w-full"
            :index="idx"
            :name="`accounts[${idx}].companyId`"
            mode="single"
            :disabled="!!companyId"
            :value="currentUser.accounts[idx].companyId"
            @onUpdated="
              ($event, i) => (currentUser.accounts[i].companyId = $event)
            "
          />
        </div>
        <div class="w-full text-left flex flex-col mt-3">
          <label for="">{{
            $t('account.accountInformations.form.role.label')
          }}</label>
          <Field
            v-slot="{ field }"
            v-model="currentUser.accounts[idx].roleId"
            :name="`accounts[${idx}].roleId`"
          >
            <multiselect
              :value="currentUser.accounts[idx].roleId"
              mode="single"
              open-direction="bottom"
              :placeholder="
                $t('account.accountInformations.form.role.placeholder')
              "
              v-bind="field"
              :options="companyRoles"
              @change="currentUser.accounts[idx].roleId = $event"
            ></multiselect>
          </Field>
        </div>
        <div
          v-if="data.value.companyId"
          class="w-full text-left flex flex-col mt-3 ml-2"
        >
          <label for="">{{
            $t('account.accountInformations.form.profile.label')
          }}</label>

          <DropdownGeneric
            :options="getCompanyContacts(currentUser.accounts[idx].companyId)"
            class="w-full"
            :name="`accounts[${idx}].contactId`"
            mode="single"
            :data="{
              name: `accounts[${idx}].contactId`,
              hint: $t('account.accountInformations.form.profile.placeholder')
            }"
            :value="currentUser.accounts[idx].contactId"
            v-model="currentUser.accounts[idx].contactId"
            :groups="false"
            @on-updated="handleContact($event, idx)"
            @search-change="handleContactSearch"
          />
        </div>
        <div class="ml-2 flex items-end">
          <i
            class="
              bi bi-trash
              px-2
              py-2
              bg-error
              text-white
              rounded
              cursor-pointer
            "
            @click="
              removeRow(data.key, data.value.id, data.value.companyId, remove)
            "
          ></i>
        </div>
      </div>
      <div class="mt-3">
        <Button
          class="btn-add h-8 flex items-center justify-center"
          variant="secondary"
          :label="$t('events.table.add')"
          @click.prevent="addRows(push)"
        >
          <i class="bi bi-plus text-lg"></i>
        </Button>
      </div>
    </FieldArray>

    <div class="relative mt-3">
      <FormActions
        position="relative"
        :errors="errors"
        :meta="meta"
        @onSubmit="handleForm(values)"
        @reset-form="$emit('onClose')"
      />
    </div>
  </Form>
</template>
<script lang="ts">
  import { computed, defineComponent, onMounted, ref, watch } from 'vue'
  import { Field, Form, FieldArray } from 'vee-validate'
  import Multiselect from '@/components/Select/multiselect.vue'
  import { InvitationForm } from '@/types'
  import { Input, Button } from '@up.law/uplaw-ui'
  import { useStore } from 'vuex'
  import { useRoute } from 'vue-router'
  import * as yup from 'yup'
  import FormActions from '@/components/Forms/ModalActions/index.vue'
  import { Contact, Role } from '@/services/api/models'
  import { useI18n } from 'vue-i18n'
  import { groupItemBy } from '@/utils'
  import { Company } from '@/store/modules/Toolbox/models'
  import { TYPE } from 'vue-toastification'
  import DropdownCompany from '@/components/Forms/DropDowns/DropdownCompany.vue'
  import DropdownGeneric from '@/components/Forms/DropDowns/DropdownGeneric.vue'
  import Checkbox from '@/components/Checkbox/index.vue'

  export default defineComponent({
    components: {
      Multiselect,
      Field,
      Form,
      Input,
      Button,
      FormActions,
      FieldArray,
      DropdownCompany,
      DropdownGeneric,
      Checkbox
    },
    props: {
      companyId: {
        type: String,
        required: false,
        default: null
      },
      account: { type: Object as () => InvitationForm, required: true }
    },
    emits: ['editAccount', 'onClose'],
    setup(props, { emit }) {
      const store = useStore()
      const route = useRoute()
      const { t, te } = useI18n()

      const companyContacts = ref<any[]>([])

      const currentUser = ref<any>({
        ...props.account,
        roles: props.account.roles,
        useMFA: props.account?.useMFA || false,
        accounts: props.account.accounts
          ? props.account.accounts
          : [
              {
                contactId: props.account?.contactId ?? '',
                roleId: props.account?.roles,
                companyId: props.account?.companyId ?? props.companyId ?? ''
              }
            ]
      })

      const hasAdminRole = computed(() => currentUser.value.roles.length > 0)

      const companies = computed<Company[]>(
        () => store.getters['organizations/loaderCompanies']
      )
      const allRoles = computed<Role[]>(() => store.getters['toolbox/roles'])

      const userRoles = computed(() => store.getters['auth/roles'])
      const profile = computed(() => store.getters['auth/profile'])
      const isUserGlobalRoute = computed(() => route.name === 'accountUsers')

      const highestPriority = computed(() =>
        Math.max(...(profile.value?.roles || []).map((x: Role) => x.priority))
      )

      const handleContact = ($event: any, index: number) => {
        currentUser.value.accounts[index].contactId = $event?.value
      }
      const addRows = (push: any) => {
        currentUser.value.accounts?.push({
          companyId: props.companyId || '',
          roleId: '',
          contactId: ''
        })
        push({
          companyId: props.companyId || '',
          roleId: '',
          contactId: ''
        })
      }
      const removeRow = (
        index: number,
        id: string,
        companyId: string,
        remove: any
      ) => {
        if (id) removeCompanyUserProfile(id, companyId, index, remove)
        else {
          remove(index)
          const elms = [...currentUser.value.accounts].filter(
            (x, i) => i !== index
          )
          currentUser.value.accounts = elms
        }
      }
      const companyRoles = computed(() =>
        allRoles.value.filter((r) => r.priority > 1)
      )
      const roles = computed(() =>
        allRoles.value
          .filter(
            (x) =>
              x.priority >=
              (![0, 2].includes(highestPriority.value)
                ? highestPriority.value + 1
                : highestPriority.value)
          )
          .filter((role) =>
            props.companyId
              ? role.priority > 1
              : [0, 1].includes(highestPriority.value)
              ? role.priority <= 1
              : role.priority > 1 && role.priority <= 7
          )
          .sort((a, b) => a.priority - b.priority)
          .map((r) => ({
            ...r,
            name: te(`roles.${r.slug}`) ? t(`roles.${r.slug}`) : r.name
          }))
      )

      const getCompanyContacts = (id: string) => {
        return allContacts.value.filter((x: Contact) => x.company?.id === id)
      }

      const handleContactSearch = (value: string) => {
        getContacts(value)
      }

      const allContacts = computed(() =>
        store.getters['auth/contacts'].filter((x: Contact) => x.company)
      )

      const contacts = computed(() => {
        const cs = groupItemBy(
          allContacts.value,
          (contact: Contact) => contact?.company?.name || 'zz-other'
        )

        const data: any[] = []
        cs.forEach(function (value, key) {
          data.push({
            id: key,
            name: key === 'zz-other' ? t('global.zz-other') : key,
            children: value
          })
        })

        return isUserGlobalRoute.value ? data : allContacts.value
      })

      const schema = computed(() =>
        yup.object().shape({
          email: yup.string().email().required(),
          firstName: yup.string().required(),
          lastName: yup.string().required(),
          roles: yup.array(yup.string()).nullable().default([]),
          accounts: yup
            .array()
            .of(
              yup.object().shape({
                contactId: yup.string().when('roleId', {
                  is: (value: string) => {
                    return allRoles.value
                      .filter((r) => r.isContactRequired)
                      .map((x) => x.id)
                      .includes(value)
                  },
                  then: (schema) => schema.required('roleMustHaveContact'),
                  otherwise: (schema) => schema.optional().nullable()
                }),
                companyId: yup.string().required('companyRequired'),
                roleId: yup.string().required('roleRequired')
              })
            )
            .default([])
        })
      )

      const getContacts = (search = '') => {
        store.dispatch('auth/GET_CONTACTS', {
          companyId: route.params.id,
          filter: {
            limit: 100,
            offset: 0,
            search
          }
        })
      }

      const getRoles = () => {
        store.dispatch('toolbox/GET_ROLES', {
          companyId: route.params.id
        })
      }

      const removeCompanyUserProfile = (
        accountId: string,
        companyId: string,
        index: number,
        removeLine: any
      ) => {
        store.dispatch('organizations/DELETE_USER_COMPANY_PROFILE', {
          userId: currentUser.value.id,
          companyAccountId: accountId,
          companyId,
          onError: () => {
            store.dispatch('toolbox/displayToast', {
              type: TYPE.ERROR,
              context: t('account.context'),
              message: t(
                `account.accountInformations.apiResponse.userCompanyAccountDeletionError`
              )
            })
          },
          onSuccess: () => {
            removeLine(index)
            store.dispatch('toolbox/displayToast', {
              type: TYPE.SUCCESS,
              context: t('account.context'),
              message: t(
                `account.accountInformations.apiResponse.userCompanyAccountDeletionSuccess`
              )
            })
          }
        })
      }

      const handleForm = (values: InvitationForm) => {
        emit('editAccount', values)
      }

      watch(props, (acc: any) => {
        currentUser.value = acc.account
      })

      onMounted(() => {
        console.log(props.account)

        getContacts()
        getRoles()
      })

      return {
        currentUser,
        isUserGlobalRoute,
        highestPriority,
        contacts,
        roles,
        schema,
        companies,
        companyRoles,
        hasAdminRole,
        handleContactSearch,
        handleContact,
        addRows,
        removeCompanyUserProfile,
        removeRow,
        companyContacts,
        getCompanyContacts,
        handleForm
      }
    }
  })
</script>
<style lang="scss" scoped>
  #account-edit-form {
    label {
      @apply uppercase text-secondary7 text-xs font-semibold text-left;
    }
  }
</style>
